import type paper from "paper";
import { seamPathData } from "./seamGeometry";
import {
  nextConstructionName,
  pieceStitchLines,
  type ConstructionLine,
  type ConstructionOutline,
  type Project,
} from "./Project";
import { newId } from "./ids";
import { withConstructionLine, withPiece, withStitchLine } from "./projectOps";
import { outlineForWholeLine } from "./constructionOutline";

/**
 * On-demand promotion of a drawn part / cutout / stitch to a
 * **construction-backed** object (#740). Each op creates a construction line
 * matching the object's current geometry and links the object to it via a
 * whole-perimeter `constructionOutline`. The commit pipeline's
 * `deriveConstructionLineKinds` + `deriveConstructionOutlines` then classify the
 * line and keep the object in sync; deleting the line later auto-bakes the object
 * back (existing behavior). The inverse of `detachTracedPiece`.
 *
 * Each op returns `null` (a no-op for the caller) when the host is missing, is
 * already construction-backed, or its geometry can't be traced into a single
 * outline (compound / degenerate) — so callers use `promote…(…) ?? proj`.
 */

/** Build the construction line + whole-perimeter outline for a path, or null. */
function buildLineAndOutline(
  project: Project,
  pathData: string,
  scope: paper.PaperScope,
): { line: ConstructionLine; outline: ConstructionOutline } | null {
  const line: ConstructionLine = {
    id: newId("con"),
    name: nextConstructionName(project),
    pathData,
  };
  const outline = outlineForWholeLine(line, scope);
  if (!outline) return null;
  return { line, outline };
}

export function promotePieceToConstruction(
  project: Project,
  pieceId: string,
  scope: paper.PaperScope,
): Project | null {
  const piece = project.pieces.find((p) => p.id === pieceId);
  if (!piece || piece.constructionOutline) return null;
  const built = buildLineAndOutline(project, piece.shapePathData, scope);
  if (!built) return null;
  const next = withConstructionLine(project, built.line);
  const target = next.pieces.find((p) => p.id === pieceId)!;
  return withPiece(next, { ...target, constructionOutline: built.outline });
}

export function promoteCutoutToConstruction(
  project: Project,
  pieceId: string,
  cutoutId: string,
  scope: paper.PaperScope,
): Project | null {
  const piece = project.pieces.find((p) => p.id === pieceId);
  const cutout = piece?.cutouts.find((c) => c.id === cutoutId);
  if (!piece || !cutout || cutout.constructionOutline) return null;
  const built = buildLineAndOutline(project, cutout.pathData, scope);
  if (!built) return null;
  const next = withConstructionLine(project, built.line);
  return {
    ...next,
    pieces: next.pieces.map((p) =>
      p.id === pieceId
        ? {
            ...p,
            cutouts: p.cutouts.map((c) =>
              c.id === cutoutId
                ? { ...c, constructionOutline: built.outline }
                : c,
            ),
          }
        : p,
    ),
  };
}

export function promoteStitchToConstruction(
  project: Project,
  pieceId: string,
  stitchId: string,
  scope: paper.PaperScope,
): Project | null {
  const piece = project.pieces.find((p) => p.id === pieceId);
  const stitch = piece
    ? pieceStitchLines(piece).find((s) => s.id === stitchId)
    : undefined;
  if (!piece || !stitch || stitch.constructionOutline) return null;
  // The seam's real shape, curves included (#982) — a polyline built from the
  // anchors would hand construction the chords between them and silently
  // straighten a curved seam on promotion.
  const pathData = seamPathData(stitch);
  const built = buildLineAndOutline(project, pathData, scope);
  if (!built) return null;
  const next = withConstructionLine(project, built.line);
  return withStitchLine(next, pieceId, {
    ...stitch,
    constructionOutline: built.outline,
  });
}
